import { type Selected, selected } from "./bot-policy-contract.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { EMPTY_TROOPS } from "./logistics-math.js";
import { type TroopComposition, UNIT_IDS } from "./logistics-types.js";
import type { SeatObservation } from "./types.js";

function strength(troops: TroopComposition): bigint {
  return (
    microFromString(troops["guard-robot"]) * 4n +
    microFromString(troops["raider-robot"]) * 3n +
    microFromString(troops["siege-gun"])
  );
}

export function botWave(o: SeatObservation, destination: string): Selected | undefined {
  const routing = o.self.logistics.reinforcement;
  const gathered = routing?.gathered[destination] ?? EMPTY_TROOPS;
  const enemy = o.seats.find((seat) => seat.id === destination);
  const opposition = strength(enemy?.logistics.borders[o.self.id]?.troops ?? EMPTY_TROOPS);
  const stationed = strength(o.self.logistics.borders[destination]?.troops ?? EMPTY_TROOPS);
  const traveling = o.self.logistics.transits
    .filter(
      (t) =>
        !t.returnAfterArrival &&
        (t.destination === destination || t.finalDestination === destination),
    )
    .reduce((sum, t) => sum + strength(t.units), 0n);
  // Never feed single new robots into an established enemy stack. A wave needs
  // five Guards' troop-fire strength and a margin against the visible opposition.
  const ready = (troops: TroopComposition) =>
    strength(troops) >= 20_000_000n &&
    (strength(troops) + stationed + traveling) * 4n >= opposition * 5n;
  if (ready(gathered))
    return selected(
      { kind: "launch-wave", borderId: destination },
      "Launch a gathered wave with a margin over visible opposition",
      destination,
    );
  const fronts = Math.max(1, Object.keys(o.self.logistics.borders).length);
  const share = Object.fromEntries(
    UNIT_IDS.map((unit) => [
      unit,
      formatMicro((microFromString(o.self.units[unit]) / BigInt(fronts)) as Micro),
    ]),
  ) as TroopComposition;
  if (ready(share))
    return selected(
      { kind: "deploy-troops", destination, units: share },
      "Deploy a reserve wave while retaining reserves for other fronts",
      destination,
    );
  return undefined;
}

export function botReinforcement(o: SeatObservation): Selected | undefined {
  const borders = Object.keys(o.self.logistics.borders)
    .filter((id) => o.seats.find((seat) => seat.id === id)?.alive)
    .sort();
  if (!borders.length) return undefined;
  if (!o.self.logistics.reinforcement)
    return selected(
      {
        kind: "route-production",
        unit: "all",
        shares: Object.fromEntries(borders.map((id) => [id, Math.floor(100 / borders.length)])),
      },
      "Share production between fronts and gather waves",
    );
  const pools = o.self.logistics.reinforcement.gathered;
  return borders
    .sort((a, b) => {
      const left = strength(pools[a] ?? EMPTY_TROOPS);
      const right = strength(pools[b] ?? EMPTY_TROOPS);
      return left > right ? -1 : left < right ? 1 : 0;
    })
    .map((id) => botWave(o, id))
    .find((wave) => wave !== undefined);
}
